import type { ServiceConnections } from "@voice/api-contract";
import type { AccountTab } from "./portal-navigation";
import {
  equipmentIdentity,
  lineIdentity,
  LineRegistration,
  type ConnectionFocus,
  type ConnectionState,
} from "./AccountConnections";

type Line = ServiceConnections["lines"][number];
type Device = ServiceConnections["devices"][number];
type TelephoneNumber = ServiceConnections["numbers"][number];
type ConnectionRow = {
  key: string;
  line?: Line;
  device?: Device;
  numbers: TelephoneNumber[];
  attention: string[];
  focus?: ConnectionFocus;
};

function connectionRows(data: ServiceConnections): ConnectionRow[] {
  const lines = new Map(data.lines.map((line) => [line.id, line]));
  const devices = new Map(data.devices.map((device) => [device.id, device]));
  const rows: ConnectionRow[] = data.lines.map((line) => {
    const device = line.deviceId ? devices.get(line.deviceId) : undefined;
    const numbers = data.numbers.filter((number) => number.lineId === line.id);
    const attention = [...line.issues];
    if (line.status === "disconnected") attention.push("Line disconnected.");
    if (!line.deviceId) attention.push("Equipment not assigned.");
    else if (!device) attention.push("Assigned equipment record is missing.");
    if (device) {
      if (["retired", "quarantined"].includes(device.status))
        attention.push(`Equipment ${device.status}.`);
      if (!device.locationId)
        attention.push("Equipment location is not recorded.");
      else if (device.locationId !== line.locationId)
        attention.push("Equipment and line have different service locations.");
      if (line.deviceSlot === null)
        attention.push("Equipment port is not recorded.");
      else if (line.deviceSlot < 1 || line.deviceSlot > device.slots)
        attention.push("Assigned port is not available on this equipment.");
      else if (
        line.status !== "disconnected" &&
        data.lines.some(
          (other) =>
            other.id !== line.id &&
            other.status !== "disconnected" &&
            other.deviceId === device.id &&
            other.deviceSlot === line.deviceSlot,
        )
      )
        attention.push(
          `Port ${line.deviceSlot} is assigned to multiple lines.`,
        );
    }
    if (line.digits && data.extensionConflicts.includes(line.digits))
      attention.push(
        "Extension conflicts with a protected short dialing code.",
      );
    for (const number of numbers) {
      if (["released", "aging", "failed"].includes(number.status))
        attention.push(`${number.number} is unavailable for calling.`);
      if (number.recordedLineId && number.recordedLineId !== line.id)
        attention.push(
          "An earlier number assignment differs. Review before changing service.",
        );
    }
    return {
      key: `line:${line.id}`,
      line,
      ...(device ? { device } : {}),
      numbers,
      attention: [...new Set(attention)],
      ...(line.status !== "disconnected" ? { focus: { lineId: line.id } } : {}),
    };
  });
  for (const number of data.numbers.filter(
    (item) => !item.lineId || !lines.has(item.lineId),
  )) {
    const attention = [
      number.lineId
        ? "Saved destination needs review."
        : "Number has no assigned line.",
    ];
    if (["released", "aging", "failed"].includes(number.status))
      attention.push("Number unavailable for calling.");
    if (number.recordedLineId)
      attention.push(
        "An earlier system assignment is recorded; reconciliation is needed.",
      );
    rows.push({
      key: `number:${number.id}`,
      numbers: [number],
      attention,
      ...(number.eligible ? { focus: { numberId: number.id } } : {}),
    });
  }
  for (const device of data.devices.filter(
    (item) => !data.lines.some((line) => line.deviceId === item.id),
  )) {
    const unavailable = ["retired", "quarantined"].includes(device.status);
    rows.push({
      key: `device:${device.id}`,
      device,
      numbers: [],
      attention: [
        unavailable
          ? `Equipment ${device.status}.`
          : "Equipment has no assigned line.",
        ...(device.recordedAssignments?.length
          ? ["An earlier system assignment is recorded; review before reuse."]
          : []),
      ],
      ...(!unavailable ? { focus: { deviceId: device.id } } : {}),
    });
  }
  return rows;
}

export function AccountConnectionOverview({
  state,
  commercial,
  customer,
  readOnly = false,
  onTab,
  onEdit,
}: {
  state: ConnectionState;
  commercial: boolean;
  customer: boolean;
  readOnly?: boolean;
  onTab: (tab: AccountTab) => void;
  onEdit?: ((focus: ConnectionFocus) => void) | undefined;
}) {
  const data = state.data;
  const rows = data ? connectionRows(data) : [];
  const mutable = !customer && !readOnly;
  return (
    <section
      className="cr-panel cr-wide cr-connection-overview"
      aria-label="Telephone numbers and equipment"
    >
      <div className="cr-account-heading">
        <div>
          <h3>
            {customer ? "Your phones and numbers" : "Numbers and equipment"}
          </h3>
          <p>Review the numbers, lines and equipment assigned together.</p>
        </div>
        <button className="cr-button secondary" onClick={state.reload}>
          Refresh connections
        </button>
      </div>
      <div className="cr-connection-overview-actions">
        <button
          className="cr-button secondary"
          onClick={() => onTab("numbers")}
        >
          {mutable ? "Assign number" : "View numbers"}
        </button>
        <button
          className="cr-button secondary"
          onClick={() => onTab("devices")}
        >
          Equipment
        </button>
        <button
          className="cr-button secondary"
          onClick={() => onTab(commercial ? "users" : "services")}
        >
          {commercial
            ? mutable
              ? "Manage extensions"
              : "View extensions"
            : mutable
              ? "Set up telephone line"
              : "View service setup"}
        </button>
      </div>
      {state.error && (
        <p className="cr-notice error" role="alert">
          Connection status could not refresh.
          {data
            ? " Showing the last received assignments; live registration is unavailable."
            : " Refresh connections to try again."}
        </p>
      )}
      {!data && !state.error && <p role="status">Loading phone connections…</p>}
      {data && rows.length === 0 && (
        <p>
          No numbers, telephone lines or equipment are recorded for this account
          yet.
        </p>
      )}
      {data && rows.length > 0 && (
        <div
          className="cr-table-wrap cr-connection-map-wrap"
          role="region"
          tabIndex={0}
          aria-label="Number, line and equipment connections"
        >
          <table className="cr-connection-map">
            <thead>
              <tr>
                <th scope="col">Incoming numbers</th>
                <th scope="col">
                  {commercial ? "Line / extension" : "Telephone line"}
                </th>
                <th scope="col">Phone or adapter</th>
                <th scope="col">Connection status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.key}
                  className={
                    row.attention.length
                      ? "cr-connection-map-attention"
                      : undefined
                  }
                >
                  <td data-label="Incoming numbers">
                    {row.numbers.length
                      ? row.numbers.map((number) => (
                          <div key={number.id}>
                            <strong>{number.number}</strong>
                            <small>{number.status.replaceAll("_", " ")}</small>
                          </div>
                        ))
                      : row.line
                        ? "No direct incoming number"
                        : "No incoming number"}
                  </td>
                  <td
                    data-label={
                      commercial ? "Line / extension" : "Telephone line"
                    }
                  >
                    {row.line ? lineIdentity(row.line) : "No line assigned"}
                  </td>
                  <td data-label="Phone or adapter">
                    {row.device ? (
                      <>
                        <span>{equipmentIdentity(row.device)}</span>
                        {row.line && row.line.deviceSlot !== null && (
                          <small>Port {row.line.deviceSlot}</small>
                        )}
                        <small>
                          {row.line?.locationName ||
                            data.locations.find(
                              (location) =>
                                location.id === row.device?.locationId,
                            )?.name ||
                            "Location not recorded"}
                        </small>
                      </>
                    ) : row.line?.deviceId ? (
                      "Equipment record unavailable"
                    ) : (
                      "Equipment not assigned"
                    )}
                  </td>
                  <td data-label="Connection status">
                    {row.line && row.line.status !== "disconnected" && (
                      <LineRegistration
                        line={
                          state.error
                            ? {
                                ...row.line,
                                observation: {
                                  registration: "unknown",
                                  configuration: "unknown",
                                  freshness: "unavailable",
                                  checkedAt: null,
                                },
                              }
                            : row.line
                        }
                      />
                    )}
                    {row.attention.length > 0 && (
                      <ul className="cr-connection-map-notes">
                        {row.attention.map((note) => (
                          <li key={note}>{note}</li>
                        ))}
                      </ul>
                    )}
                    {mutable && onEdit && row.focus && (
                      <button
                        className="cr-text-button"
                        onClick={() => onEdit(row.focus!)}
                      >
                        {row.line ? "Change assignment" : "Assign to a line"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="cr-form-note">
        Assignments show the saved setup. SIP registration and installed
        configuration are checked separately; they do not verify call audio or
        emergency calling.
      </p>
    </section>
  );
}
